import { useRef, useState, useCallback } from 'react';

export const useCardTilt = (intensity: number = 15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tiltStyle, setTiltStyle] = useState({});
  const [glowPosition, setGlowPosition] = useState({ x: 50, y: 50 });
  const rafRef = useRef<number | null>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      if (!ref.current) return;
      
      const card = ref.current;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / intensity;
      const rotateY = (centerX - x) / intensity;
      
      // Calculate glow position percentage
      const glowX = (x / rect.width) * 100;
      const glowY = (y / rect.height) * 100;
      setGlowPosition({ x: glowX, y: glowY });

      setTiltStyle({
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`,
        transition: 'transform 0.1s ease-out',
        willChange: 'transform',
        boxShadow: `
          0 25px 50px -12px rgba(0, 0, 0, 0.25),
          ${rotateY * 2}px ${rotateX * 2}px 30px rgba(226, 78, 33, 0.15)
        `,
      });
    });
  }, [intensity]);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    setGlowPosition({ x: 50, y: 50 });
    setTiltStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
      transition: 'transform 0.5s ease-out, box-shadow 0.5s ease-out',
      willChange: 'auto',
      boxShadow: '0 10px 30px -15px rgba(0, 0, 0, 0.2)',
    });
  }, []);

  return {
    ref,
    tiltStyle,
    glowPosition,
    handleMouseMove,
    handleMouseLeave,
  };
};
